import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import getAccessToken from 'hooks/getAccessToken'
import { request } from 'hooks/useQuery'
import { CUIT_LENGTH, formatTaxAddress, isValidCUIT, normalizeCUIT } from 'utils/cuit'
import styles from './TaxIdentity.module.scss'

// La API ya responde 404 si faltan datos; se repite acá para no mostrar nunca
// un panel confirmable vacío (RF-02).
const isCompleteTaxInfo = taxInfo => !!(taxInfo?.cuit && taxInfo.legalName && taxInfo.address?.street)

const statusForLookupError = error => {
  if (error?.status === 404) return 'notFound'
  if (error?.status === 422) return 'invalid'
  return 'unavailable'
}

function TaxInfoRows({ taxInfo }) {
  const { t } = useTranslation()
  return (
    <dl className={styles.rows}>
      <dt>{t('subscriptions_tax_cuit', 'CUIT')}</dt>
      <dd>{taxInfo.cuit}</dd>
      <dt>{t('subscriptions_tax_legal_name', 'Razón social')}</dt>
      <dd>{taxInfo.legalName}</dd>
      <dt>{t('subscriptions_tax_address', 'Domicilio')}</dt>
      <dd>{formatTaxAddress(taxInfo.address)}</dd>
    </dl>
  )
}

// El alta solo avanza con datos fiscales que devolvió Contabilium y que el
// usuario confirmó explícitamente: onChange recibe null ante cualquier cambio.
export function TaxIdentity({ onChange }) {
  const { t } = useTranslation()
  const accessToken = getAccessToken()
  const [cuit, setCuit] = useState('')
  const [status, setStatus] = useState('idle')
  const [taxInfo, setTaxInfo] = useState(null)
  const latestLookupID = useRef(0)

  const restart = nextCuit => {
    latestLookupID.current += 1
    setCuit(nextCuit)
    setStatus('idle')
    setTaxInfo(null)
    onChange(null)
  }

  const search = async () => {
    if (!isValidCUIT(cuit)) {
      setStatus('invalid')
      return
    }

    latestLookupID.current += 1
    const lookupID = latestLookupID.current
    setStatus('loading')

    try {
      const result = await request(window._l(`/subscriptions/tax-info/${cuit}`).slice(1), accessToken.value)
      if (lookupID !== latestLookupID.current) return
      if (!isCompleteTaxInfo(result)) {
        setStatus('notFound')
        return
      }
      setTaxInfo(result)
      setStatus('review')
    } catch (error) {
      if (lookupID !== latestLookupID.current) return
      console.error('[subscriptions] tax-info lookup failed', error?.status)
      setStatus(statusForLookupError(error))
    }
  }

  const confirm = () => {
    setStatus('confirmed')
    onChange(taxInfo)
  }

  const onKeyDown = event => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    search()
  }

  if (status === 'confirmed') {
    return (
      <section className={styles.panel} aria-label={t('subscriptions_tax_confirmed_title', 'Datos fiscales confirmados')}>
        <div className={styles.header}>
          <h6 className={styles.title}>{t('subscriptions_tax_confirmed_title', 'Datos fiscales confirmados')}</h6>
          <button type="button" className={styles.linkButton} onClick={() => restart(cuit)}>
            {t('subscriptions_tax_change', 'Cambiar CUIT')}
          </button>
        </div>
        <TaxInfoRows taxInfo={taxInfo} />
      </section>
    )
  }

  const isLoading = status === 'loading'
  const errorMessage = {
    invalid: t('subscriptions_tax_invalid_cuit', 'Ingresá un CUIT válido de 11 dígitos'),
    notFound: t('subscriptions_tax_not_found', 'No fue posible validar el CUIT. Revisalo y volvé a consultar.'),
    unavailable: t('subscriptions_tax_unavailable', 'No pudimos consultar los datos fiscales en este momento. Intentá nuevamente.'),
  }[status]

  return (
    <section className={styles.section}>
      <label htmlFor="billingTaxID" className="form-label">{t('subscriptions_tax_cuit', 'CUIT')}</label>
      <div className={styles.searchRow}>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder={t('subscriptions_tax_cuit_placeholder', 'Ej: 20123456789')}
          className={`form-control ${styles.input} ${errorMessage ? 'is-invalid' : ''}`}
          id="billingTaxID"
          value={cuit}
          aria-describedby="billingTaxIDHelp"
          // Sin maxLength: el browser recortaría un CUIT pegado con guiones
          // antes de normalizarlo y quedarían menos de 11 dígitos.
          onChange={event => restart(normalizeCUIT(event.target.value).slice(0, CUIT_LENGTH))}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          className={styles.primaryButton}
          disabled={isLoading || cuit.length !== CUIT_LENGTH || !accessToken.value}
          onClick={search}
        >
          {isLoading && <span className="spinner-border spinner-border-sm" role="status"></span>}
          {status === 'unavailable' ? t('subscriptions_tax_retry', 'Reintentar') : t('subscriptions_tax_search', 'Buscar')}
        </button>
      </div>
      <div id="billingTaxIDHelp" className={styles.help}>
        <span>{t('subscriptions_tax_cuit_hint', 'El CUIT debe tener 11 números')}</span>
        <span>{cuit.length}/{CUIT_LENGTH}</span>
      </div>
      <div aria-live="polite">
        {errorMessage && <div className={styles.error}>{errorMessage}</div>}
      </div>

      {status === 'review' && (
        <div className={styles.panel}>
          <h6 className={styles.title}>{t('subscriptions_tax_review_title', 'Confirmar datos')}</h6>
          <p className={styles.subtitle}>{t('subscriptions_tax_review_subtitle', 'Por favor confirmá los siguientes datos:')}</p>
          <TaxInfoRows taxInfo={taxInfo} />
          <div className={styles.footer}>
            <button type="button" className={styles.secondaryButton} onClick={() => restart(cuit)}>
              {t('subscriptions_tax_back', 'Volver')}
            </button>
            <button type="button" className={styles.primaryButton} onClick={confirm}>
              {t('subscriptions_tax_confirm', 'Confirmar')}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
